import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Receipt, Clock, ArrowLeft, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { blogPosts, getCategoryColor } from '../data/blogData';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) {
      document.title = `${post.title} | FinScan Blog`;
    }
  }, [slug, post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-indigo-400 hover:text-indigo-300">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `https://finscan.store/blog/${post.slug}`;
  const pageTitle = `${post.title} | FinScan Blog`;

  return (
    <>
      <Helmet>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={`https://finscan.store/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={`https://finscan.store/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
      </Helmet>

      <div className="min-h-screen bg-[#030712]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2">
                <Receipt className="w-8 h-8 text-indigo-400" />
                <span className="text-2xl font-bold font-['Outfit'] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                  FinScan
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link to="/blog" className="text-muted-foreground hover:text-white transition-colors">
                  Blog
                </Link>
                <Link to="/auth" className="bg-white text-black hover:bg-white/90 font-medium px-4 py-2 rounded-full transition-all">
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Article */}
        <article className="pt-32 pb-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Link to="/blog" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
            </motion.div>

            {/* Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(post.category)}`}>
                  {post.category}
                </span>
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  {post.readTime} read
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
                {post.title}
              </h1>
              
              <p className="text-muted-foreground">
                Published on {post.date}
              </p>
            </motion.header>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div className="blog-content text-white/90 leading-relaxed">
                {post.content.split('\n\n').map((paragraph, i) => {
                  if (paragraph.startsWith('## ')) {
                    return (
                      <h2 key={i} className="text-2xl font-bold mt-10 mb-4 text-white">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (paragraph.startsWith('### ')) {
                    return (
                      <h3 key={i} className="text-xl font-semibold mt-8 mb-3 text-white">
                        {paragraph.replace('### ', '')}
                      </h3>
                    );
                  }
                  if (paragraph.startsWith('- ')) {
                    const items = paragraph.split('\n').filter(item => item.startsWith('- '));
                    return (
                      <ul key={i} className="list-disc pl-6 my-4 space-y-2">
                        {items.map((item, j) => (
                          <li key={j} className="text-white/80">{item.replace('- ', '')}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (paragraph.startsWith('1. ')) {
                    const items = paragraph.split('\n').filter(item => /^\d+\. /.test(item));
                    return (
                      <ol key={i} className="list-decimal pl-6 my-4 space-y-2">
                        {items.map((item, j) => (
                          <li key={j} className="text-white/80">{item.replace(/^\d+\. /, '')}</li>
                        ))}
                      </ol>
                    );
                  }
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <p key={i} className="font-semibold text-indigo-400 my-4">
                        {paragraph.replace(/\*\*/g, '')}
                      </p>
                    );
                  }
                  return (
                    <p key={i} className="my-4 text-white/80">
                      {paragraph.split('**').map((part, j) => 
                        j % 2 === 1 ? <strong key={j} className="text-white">{part}</strong> : part
                      )}
                    </p>
                  );
                })}
              </div>
            </motion.div>

            {/* Share */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-12 pt-8 border-t border-white/10"
            >
              <div className="flex items-center gap-4">
                <span className="text-muted-foreground">Share this article:</span>
                <div className="flex gap-2">
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-12 p-8 bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent border border-indigo-500/30 rounded-2xl text-center"
            >
              <h3 className="text-2xl font-bold mb-3">Ready to track smarter?</h3>
              <p className="text-muted-foreground mb-6">
                FinScan helps you scan receipts, create invoices, and organize your finances — all for free.
              </p>
              <Link
                to="/auth"
                className="inline-block bg-indigo-500 text-white hover:bg-indigo-400 font-medium px-8 py-3 rounded-full transition-all"
              >
                Get Started Free
              </Link>
            </motion.div>
          </div>
        </article>

        {/* Related Posts */}
        <section className="py-16 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold mb-8">More Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogPosts
                .filter(p => p.slug !== post.slug)
                .slice(0, 3)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors"
                  >
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(relatedPost.category)}`}>
                      {relatedPost.category}
                    </span>
                    <h3 className="text-lg font-semibold mt-3 line-clamp-2">{relatedPost.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2">{relatedPost.readTime}</p>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-sm">
                © 2026 FinScan. Built for independent workers.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/electricians" className="text-muted-foreground hover:text-white transition-colors">Contractors</Link>
                <Link to="/1099" className="text-muted-foreground hover:text-white transition-colors">1099 Workers</Link>
                <Link to="/freelancers" className="text-muted-foreground hover:text-white transition-colors">Freelancers</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPost;
